import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { cn, Tabs, TabsList, TabsTrigger } from "@tellescope/ui";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const ALL_VALUE = "all" as const;

export interface SharedPhoneNumberToggleProps {
  phoneNumber: string;
  contactName: string;
  showAll: boolean;
  onToggle: (newVal: boolean) => void;
  className?: string;
  height?: string;
}

export const SharedPhoneNumberToggle = ({
  phoneNumber,
  contactName,
  showAll,
  onToggle,
  className,
  height = "h-8",
}: SharedPhoneNumberToggleProps) => {
  const { t } = useTranslation("messaging");

  const endusersWithPhoneNumberQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      mdbFilter: {
        $or: [{ phone: phoneNumber }, { alternatePhones: phoneNumber }],
      },
    },
    select: (data) => data.pages.flat().length,
    enabled: !!phoneNumber,
  });

  const phoneNumberIsShared = useMemo(() => {
    return (endusersWithPhoneNumberQuery.data ?? 0) > 1;
  }, [endusersWithPhoneNumberQuery.data]);

  if (!phoneNumber) return null;

  return (
    <div className={cn("transition-all", phoneNumberIsShared ? height : "h-0", className)}>
      <div className={cn("flex justify-between items-center", phoneNumberIsShared ? "visible" : "hidden")}>
        <p className="text-xs text-muted-foreground">{t("Other conversations associated with this phone number")}</p>
        <Tabs value={showAll ? ALL_VALUE : contactName} onValueChange={(val) => onToggle(val === ALL_VALUE)}>
          <TabsList className="h-min">
            <TabsTrigger value={contactName} className="text-xs p-1.5">
              {contactName}
            </TabsTrigger>
            <TabsTrigger value={ALL_VALUE} className="text-xs p-1.5">
              {t("All")}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
    </div>
  );
};
